import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { inventoryApi } from '../../api/inventoryApi';
import { formatQuantity } from '../../utils/formatQuantity';
import type { ThemeColors } from '../../context/ThemeContext';

const formatQty = (qty: number, unit: string) => formatQuantity(qty ?? 0, unit);

interface VarianceRow {
  inventoryItemId: number;
  name: string;
  unit: string;
  theoreticalUse: number;
  recordedDeductions: number;
  wastage: number;
  countAdjustment: number;
  unexplained: number;
}

interface VarianceReport {
  from: string;
  to: string;
  source: 'POS' | 'MANUAL';
  rows: VarianceRow[];
}

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

interface VarianceReportTabProps {
  theme: ThemeColors;
  /** when provided/changed, prefill the date fields and auto-run the report */
  initialRange?: { from: string; to: string } | null;
}

const toISODate = (d: Date) => d.toISOString().slice(0, 10);

/** A gap counts as notable when it exceeds 10% of expected use (and there was use). */
const isBigGap = (row: VarianceRow) =>
  row.theoreticalUse > 0 && Math.abs(row.unexplained) > 0.1 * row.theoreticalUse;

/**
 * Consumption variance report: compares what recipes say sales should have used
 * against the stock movements actually recorded, surfacing unexplained gaps.
 */
const VarianceReportTab: React.FC<VarianceReportTabProps> = ({ theme, initialRange }) => {
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);

  const [from, setFrom] = useState<string>(toISODate(weekAgo));
  const [to, setTo] = useState<string>(toISODate(today));
  const [report, setReport] = useState<VarianceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runReport = useCallback(async (rangeFrom: string, rangeTo: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryApi.getConsumptionVariance(rangeFrom, rangeTo);
      setReport({
        from: res.data?.from ?? rangeFrom,
        to: res.data?.to ?? rangeTo,
        source: res.data?.source ?? 'MANUAL',
        rows: res.data?.rows || [],
      });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  }, []);

  // When a new date range is pushed in (e.g. "View variance" from a day card),
  // prefill the fields and run the report immediately.
  const lastAppliedRange = useRef<string | null>(null);
  useEffect(() => {
    if (!initialRange) return;
    const key = `${initialRange.from}|${initialRange.to}`;
    if (lastAppliedRange.current === key) return;
    lastAppliedRange.current = key;
    setFrom(initialRange.from);
    setTo(initialRange.to);
    runReport(initialRange.from, initialRange.to);
  }, [initialRange, runReport]);

  const sourceLabel = (source: 'POS' | 'MANUAL') =>
    source === 'POS' ? 'From POS orders' : 'From manual sales';

  const cardBorder = theme.components?.card?.border;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{
        backgroundColor: theme.components?.card?.background,
        border: `1px solid ${cardBorder}`,
        borderRadius: 2,
        mb: 3,
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
            Consumption Variance
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
            Compares what your sales should have used (recipes × sales) against recorded stock
            movements. Large unexplained gaps usually mean stock ran out mid-day, missing recipes,
            or unrecorded wastage.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'variance-from' }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'variance-to' }}
            />
            <Button
              variant="contained"
              onClick={() => runReport(from, to)}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <ChartBarIcon className="h-4 w-4" />}
              data-testid="variance-run"
              sx={{
                backgroundColor: `${theme.accent}22 !important`,
                color: `${theme.accent} !important`,
                border: `1px solid ${theme.accent} !important`,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none !important',
                '&:hover': { backgroundColor: `${theme.accent}44 !important`, boxShadow: 'none !important' },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              Run report
            </Button>
          </Box>
        </CardContent>
      </Card>

      {report && (
        <Card sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${cardBorder}`,
          borderRadius: 2,
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                Results
              </Typography>
              <Chip
                label={sourceLabel(report.source)}
                size="small"
                data-testid="variance-source-chip"
                sx={{
                  backgroundColor: `${theme.primary}18`,
                  color: theme.primary,
                  fontWeight: 600,
                }}
              />
            </Box>

            {report.rows.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.gray?.[600], py: 3, textAlign: 'center' }}>
                No consumption in this period.
              </Typography>
            ) : isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {report.rows.map((row) => {
                  const flagged = isBigGap(row);
                  return (
                    <Box
                      key={row.inventoryItemId}
                      data-testid="variance-row"
                      sx={{
                        p: 1.5, borderRadius: 1.5,
                        border: `1px solid ${flagged ? theme.warning : cardBorder}`,
                        backgroundColor: flagged ? `${theme.warning}10` : theme.background,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{row.name}</Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: flagged ? theme.warning : theme.text }}
                        >
                          Gap {formatQty(row.unexplained, row.unit)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                          Expected {formatQty(row.theoreticalUse, row.unit)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                          Deducted {formatQty(row.recordedDeductions, row.unit)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                          Wastage {formatQty(row.wastage, row.unit)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                          Count adj. {formatQty(row.countAdjustment, row.unit)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }}>Ingredient</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Expected use</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Deducted</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Wastage</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Count adj.</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Gap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.rows.map((row) => {
                      const flagged = isBigGap(row);
                      return (
                        <TableRow
                          key={row.inventoryItemId}
                          data-testid="variance-row"
                          sx={flagged ? { backgroundColor: `${theme.warning}10` } : {}}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{row.name}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.text }}>
                              {formatQty(row.theoreticalUse, row.unit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                              {formatQty(row.recordedDeductions, row.unit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                              {formatQty(row.wastage, row.unit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                              {formatQty(row.countAdjustment, row.unit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: flagged ? theme.warning : theme.text }}
                            >
                              {formatQty(row.unexplained, row.unit)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VarianceReportTab;
